import SalesDealsApi from 'api/admins/sales-deals-api'
import { TitlePage } from 'components/atoms'
import SalesDealsView from 'domains/sales/deals/view/sales-deal-view'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const SalesDealsDetailPage = ({ pageUtils }) => {
  const { id } = useParams()
  const [ deal, setDeal ] = useState({})
  const [ historyList, setHistoryList ] = useState([])
  const [ historyDetail, setHistoryDetail ] = useState({})
  const [ isLoading, setIsLoading ] = useState(true)

  const extractApiErrorMessage = (error, fallback = 'Oops, Something wrong') => {
    const data = error?.response?.data

    if (typeof data === 'string' && data.trim()) return data
    if (typeof data?.message === 'string' && data.message.trim()) return data.message
    if (Array.isArray(data?.message) && data.message.length > 0) return data.message.join(', ')
    if (Array.isArray(data?.errors) && data.errors.length > 0) return data.errors.join(', ')

    if (data?.errors && typeof data.errors === 'object') {
      const flattened = Object.values(data.errors).flat().filter(Boolean)
      if (flattened.length > 0) return flattened.join(', ')
    }

    if (typeof error?.message === 'string' && error.message.trim()) return error.message

    return fallback
  }

  const cancelDealItem = (payload) => {
    setIsLoading(true)

    return SalesDealsApi.cancelItem({ dealId: id, payload })
      .then((response) => {
        if (response?.data?.data) {
          setDeal(response.data.data)
        }
        pageUtils.setAlertMsg(response.data?.message || 'Deal item cancelled successfully.')
        return response
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(extractApiErrorMessage(error, 'Failed to cancel deal item.'))
        throw error
      })
      .finally(() => { setIsLoading(false) })
  }

  const cancelDeal = () => {
    setIsLoading(true)

    return SalesDealsApi.cancel({ dealId: id })
      .then((response) => {
        if(response.status === 200){
          pageUtils.setAlertMsg(response.data?.message || 'Deal cancelled successfully.')
          return fetchDeal().then(() => response)
        }
        return response
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(extractApiErrorMessage(error, 'Failed to cancel deal.'))
        throw error
      })
      .finally(() => { setIsLoading(false) })
  }

  const fetchDeal = () => {
    setIsLoading(true)

    return SalesDealsApi.show({ dealId: id })
      .then((response) => { setDeal(response.data.data) })
      .catch((error) => pageUtils.setApiErrorMsg(extractApiErrorMessage(error, 'Failed to load deal.')))
      .finally(() => { setIsLoading(false) })
  }

  const updateDeal = (payload) => {
    setIsLoading(true)

    return SalesDealsApi.update({ dealId: id, payload })
      .then((response) => {
        setDeal(response.data.data)
        pageUtils.setAlertMsg(response.data.message || 'Deal updated successfully.')
        return response
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(extractApiErrorMessage(error, 'Failed to update deal.'))
        throw error
      })
      .finally(() => { setIsLoading(false) })
  }

  const updateFinalPrices = (payload) => {
    setIsLoading(true)

    return SalesDealsApi.updateFinalPrices({ dealId: id, payload })
      .then((response) => {
        setDeal(response.data.data)
        pageUtils.setAlertMsg(response.data.message || 'Final price updated successfully.')
        return response
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(extractApiErrorMessage(error, 'Failed to update final price.'))
        throw error
      })
      .finally(() => { setIsLoading(false) })
  }

  const fetchHistoryList = () => {
    SalesDealsApi.listHistory({ dealId: id })
      .then((response) => {
        setHistoryList(
          (response.data?.data || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        )
      })
      .catch((error) => pageUtils.setApiErrorMsg(extractApiErrorMessage(error, 'Failed to load history list.')))
  }

  const fetchHistoryDetail = (historyId) => {
    SalesDealsApi.getHistory({ historyId })
      .then((response) => { setHistoryDetail(response.data.data) })
      .catch((error) => pageUtils.setApiErrorMsg(extractApiErrorMessage(error, 'Failed to load history detail.')))
  }

  const clearHistoryDetail = () => {
    setHistoryDetail({})
  }

  useEffect(() => {
    fetchDeal()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ id ])

  return (
    <>
      <TitlePage mainTitle={ 'Sales Deal' }/>
      <SalesDealsView
        cancelDeal={ cancelDeal }
        cancelDealItem={ cancelDealItem }
        fetchDeal={ fetchDeal }
        updateDeal={ updateDeal }
        updateFinalPrices={ updateFinalPrices }
        fetchHistoryList={ fetchHistoryList }
        fetchHistoryDetail={ fetchHistoryDetail }
        clearHistoryDetail={ clearHistoryDetail }
        deal={ deal }
        historyList={ historyList }
        historyDetail={ historyDetail }
        isLoading={ isLoading }
      />
    </>
  )
}

SalesDealsDetailPage.propTypes = propTypes

export default SalesDealsDetailPage
